import { Button, IconButton, MenuItem } from "@mui/material";
import { StateHandle, arrayIndexHandle } from "StateHandle";
import { CellType, CellValuePredicateInput, CellValuePredicateType, FormField } from "generated/graphql";
import { Dispatch, SetStateAction } from "react";
import PSelect from "components/PSelect";
import { FormFieldCellEditor } from "./FormField/FormFieldCellEditor";
import { cellValueFromGQL, gqlCellValueFromValue } from "Backend";
import produce from "immer";
import { Close } from "@mui/icons-material";

const displayPredicateType = (type: CellValuePredicateType) => {
  switch (type) {
    case CellValuePredicateType.Equals:
      return "Equals";
    case CellValuePredicateType.IsInRange:
      return "Range";
    case CellValuePredicateType.ArrayContainsAll:
      return "List Contains";
  }
};

const CellValuePredicateBuilder = ({
  field,
  predicateHandle,
}: {
  field: FormField;
  predicateHandle: StateHandle<CellValuePredicateInput>;
}) => {
  const [predicate, setPredicate] = predicateHandle;
  const currentCell = field.cells.find((cell) => cell.id === predicate.cellId);
  return (
    <div className="flex flex-row gap-1 border rounded m-1 p-1">
      <div className="flex flex-row gap-1 items-center">
        <PSelect
          value={predicate.cellId}
          onChange={(evt) => setPredicate((prev) => ({ ...prev, cellId: evt.target.value as number }))}>
          {field.cells.map((cell) => (
            <MenuItem key={cell.id} value={cell.id}>
              {cell.name}
            </MenuItem>
          ))}
        </PSelect>

        <PSelect
          value={predicate.predType}
          onChange={(evt) =>
            setPredicate((prev) => ({ ...prev, predType: evt.target.value as CellValuePredicateType }))
          }>
          {Object.values(CellValuePredicateType).map((type) => (
            <MenuItem key={type} value={type}>
              {displayPredicateType(type)}
            </MenuItem>
          ))}
        </PSelect>
      </div>
      {currentCell !== undefined && (
        <div className="flex flex-row items-center overflow-auto">
          {predicate.predType === CellValuePredicateType.Equals ? (
            <FormFieldCellEditor
              caseInfo={undefined}
              cell={currentCell}
              value={cellValueFromGQL(predicate.predEquals)}
              setValue={(value) =>
                setPredicate((prev) => ({ ...prev, predEquals: gqlCellValueFromValue(currentCell.type, value) }))
              }
              error=""
              setError={() => {}}
              disabled={false}
              tabular={false}
              label="value"
              variant={undefined}
              isValid={undefined}
              displayEmptyOption={true}
            />
          ) : predicate.predType === CellValuePredicateType.IsInRange ? (
            <>
              <FormFieldCellEditor
                caseInfo={undefined}
                cell={currentCell}
                value={cellValueFromGQL(predicate?.predIsInRange?.min)}
                setValue={(value) =>
                  setPredicate((prev) =>
                    produce(prev, (draft) => {
                      draft.predIsInRange = draft.predIsInRange ?? {};
                      draft.predIsInRange.min = gqlCellValueFromValue(currentCell.type, value);
                      return draft;
                    })
                  )
                }
                error=""
                setError={() => {}}
                disabled={false}
                tabular={false}
                label="min"
                variant={undefined}
                isValid={undefined}
                displayEmptyOption={true}
              />
              <p className="whitespace-pre"> - </p>
              <FormFieldCellEditor
                caseInfo={undefined}
                cell={currentCell}
                value={cellValueFromGQL(predicate?.predIsInRange?.max)}
                setValue={(value) =>
                  setPredicate((prev) =>
                    produce(prev, (draft) => {
                      draft.predIsInRange = draft.predIsInRange ?? {};
                      draft.predIsInRange.max = gqlCellValueFromValue(currentCell.type, value);
                      return draft;
                    })
                  )
                }
                error=""
                setError={() => {}}
                disabled={false}
                tabular={false}
                label="max"
                variant={undefined}
                isValid={undefined}
                displayEmptyOption={true}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export const CellValuePredicateList = ({
  field,
  values,
  setValues,
}: {
  field: FormField;
  values: CellValuePredicateInput[];
  setValues: Dispatch<SetStateAction<CellValuePredicateInput[]>>;
}) => {
  const makeSetNthPredicate = (index: number) => arrayIndexHandle([values, setValues], index);

  return (
    <div className="flex flex-row items-center">
      {values.map((_, index) => {
        const last = index === values.length - 1;
        return (
          <div key={index} className="flex flex-row items-center">
            <CellValuePredicateBuilder field={field} predicateHandle={makeSetNthPredicate(index)!} />
            <IconButton size="small" onClick={() => setValues(values.filter((_, i) => i !== index))}>
              <Close />
            </IconButton>
            {!last && <p>OR</p>}
          </div>
        );
      })}
      <Button
        size="small"
        variant="text"
        onClick={() =>
          setValues([
            ...values,
            {
              predType: CellValuePredicateType.Equals,
              cellId: field.cells[0]?.id ?? -1,
              predEquals: { type: CellType.Text },
            },
          ])
        }>
        + Condition
      </Button>
    </div>
  );
};

export const CellValuePredicateBooleanSet = ({
  field,
  values,
  setValues,
}: {
  field: FormField;
  values: CellValuePredicateInput[][];
  setValues: Dispatch<SetStateAction<CellValuePredicateInput[][]>>;
}) => {
  // Displays a list of CellValuePredicateList, where the outer list is ANDed together
  // and the predicates within each list are ORed together.

  return (
    <div className="flex flex-row max-w-[600px] overflow-x-auto">
      {values.map((_, index) => {
        const handle = arrayIndexHandle([values, setValues], index)!;
        const last = index === values.length - 1;
        return (
          <div key={index} className="flex flex-row items-center">
            <div className="flex flex-row border">
              <CellValuePredicateList key={index} field={field} values={handle[0]} setValues={handle[1]} />
            </div>
            <IconButton size="small" onClick={() => setValues(values.filter((_, i) => i !== index))}>
              <Close />
            </IconButton>
            {!last && <p>AND</p>}
          </div>
        );
      })}
      <Button
        size="small"
        variant="text"
        onClick={() =>
          setValues([
            ...values,
            [
              {
                predType: CellValuePredicateType.Equals,
                cellId: field.cells[0]?.id ?? -1,
                predEquals: { type: CellType.Text },
              },
            ],
          ])
        }>
        + Condition Group
      </Button>
    </div>
  );
};
